import { Paper, Text, Flex, Button } from '@mantine/core';
import { IconChevronRight, IconFileText } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { CalendarEvent } from 'src/meetings/models';
import { getParticipantFullName } from '../utils/event-utils';
import { EventTime } from './EventTime';

type Props = {
  event: CalendarEvent;
};

export const PastEventCard = ({ event }: Props) => {
  const firstClientParticipant = event.eventParticipants.find(
    p => p.participant.participantType === 'CLIENT',
  );
  const clientName = getParticipantFullName(firstClientParticipant) ?? 'Unknown Client';

  return (
    <Paper variant="card" p="md">
      <Flex justify="space-between" align="center">
        <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
          {clientName}
        </Text>
        <EventTime
          scheduledStartAt={event.scheduledStartAt}
          scheduledEndAt={event.scheduledEndAt}
          startAt={event.startAt}
          endAt={event.endAt}
          platform={event.meetingPlatform}
        />
      </Flex>

      <Link
        to={`/meetings/${event.id}/summary`}
        style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10" mt="xxxs">
          {event.title}
        </Text>

        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6" mt="xxs" lineClamp={2}>
          {event.summary}
        </Text>
      </Link>

      <Flex
        justify="space-between"
        align="center"
        mt="sm"
        pt="sm"
        style={{ borderTop: '1px solid #f2f2f2' }}
      >
        <MeetingNoteButton event={event} />
        <Button
          component={Link}
          style={{ overflow: 'visible' }}
          to={`/meetings/${event.id}/summary`}
          variant="text-action"
          rightSection={<IconChevronRight size={16} />}
        >
          More actions
        </Button>
      </Flex>
    </Paper>
  );
};

function MeetingNoteButton({ event }: { event: CalendarEvent }) {
  if (!event.hasMeetingNote) {
    return (
      <Button
        component={Link}
        style={{ overflow: 'visible' }}
        to={`/meetings/${event.id}/meeting-note`}
        variant="text-action"
      >
        Generate meeting note
      </Button>
    );
  }

  return (
    <Button
      component={Link}
      style={{
        overflow: 'visible',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textDecorationThickness: '15%',
        textUnderlineOffset: '17%',
      }}
      to={`/meetings/${event.id}/meeting-note`}
      variant="text-action"
    >
      <IconFileText size={16} style={{ marginRight: 'var(--mantine-spacing-xxxs)' }} />
      Meeting note
    </Button>
  );
}
